import React, { useState, useEffect } from "react";

// Components
import Sidebar from "../../components/Sidebar";
import Main from "./TbSetItem/Main";
import Form from "./TbSetItem/Form";

// Utils
import { getDataWithComp, getAlert } from "../../../utils/SamuiUtils";
import { InitialDataTbSetItem } from "../../../model/InitialData/InitialDataTbSetItem";

const TbSetItem = () => {
  const [mode, setMode] = useState("");
  const [dataMasterList, setDataMasterList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [formData, setFormData] = useState(InitialDataTbSetItem());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState(null);

  useEffect(() => {
    initialize();
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const rows = await getDataWithComp("Tb_Set_ItmGroup", "AND Group_Status = 'Y' ORDER BY Group_Name ASC");
      setGroups(Array.isArray(rows) ? rows : []);
    } catch {}
  };

  const initialize = async () => {
    try {
      setMode("S");
      setCurrentPage(1);
      await fetchRealtime({ page: 1, includeTotal: true });
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    await fetchRealtime({ page, includeTotal: false });
  };

  const handleItemsPerPageChange = async (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
    await fetchRealtime({ page: 1, perPage, includeTotal: true });
  };

  const handleSearchChange = async (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    await fetchRealtime({ page: 1, term, includeTotal: true });
  };

  const handleGroupChange = async (groupId) => {
    setFilterGroup(groupId);
    setCurrentPage(1);
    await fetchRealtime({ page: 1, group: groupId, includeTotal: true });
  };

  const buildSearchWhere = (term, group = filterGroup) => {
    let where = "";
    const keyword = String(term ?? "").trim();
    if (keyword) {
      const normalized = keyword.replace(/\s+/g, " ");
      where += `AND (
        Item_Code LIKE '%${normalized}%'
        OR Item_Name LIKE '%${normalized}%'
        OR Item_Short_Name LIKE '%${normalized}%'
        OR Item_Unit_ST LIKE '%${normalized}%'
        OR Item_Cost LIKE '%${normalized}%'
        OR Item_Price LIKE '%${normalized}%'
      ) `;
    }
    if (group != null) {
      where += `AND Item_Group = ${Number(group)} `;
    }
    return where;
  };

  const fetchRealtime = async ({
    page = currentPage,
    perPage = itemsPerPage,
    term = searchTerm,
    group = filterGroup,
    includeTotal = true,
  } = {}) => {
    try {
      setIsLoading(true);
      const where = buildSearchWhere(term, group);
      const masterList = await getDataWithComp(
        "View_Set_Item",
        `${where} ORDER BY Item_Short_Name, Item_Brand, Item_Series, Item_Colour, Size_Line, Item_Id`,
        {
          fields: [
            "Item_Id",
            "Item_Code",
            "Item_Group",
            "Item_Name",
            "Item_Short_Name",
            "Item_Unit_ST",
            "Item_Cost",
            "Item_Price",
            "Item_Img",
          ],
          limit: perPage,
          offset: (page - 1) * perPage,
          includeTotal,
        }
      );
      const list = Array.isArray(masterList) ? masterList : [];
      if (includeTotal) {
        const total = Number(list?.[0]?.Total_Count ?? 0);
        setTotalItems(Number.isNaN(total) ? 0 : total);
      }
      setDataMasterList(list);
    } catch (error) {
      getAlert("FAILED", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onPageInsert = () => {
    setMode("I");
  };

  const fetchItemDetail = async (itemId) => {
    if (!itemId) return null;
    const rows = await getDataWithComp(
      "Tb_Set_Item",
      `AND Item_Id = ${itemId} ORDER BY Item_Id`
    );
    return Array.isArray(rows) && rows.length ? rows[0] : null;
  };

  const onRowSelected = async (item) => {
    setMode("U");
    const detail = await fetchItemDetail(item?.Item_Id);
    const source = detail || item;
    setFormData({
      item_id: source.Item_Id,
      comp_id: source.Comp_Id,
      item_barcode: source.Item_Barcode,
      item_code: source.Item_Code,
      item_name: source.Item_Name,
      item_name_en: source.Item_Name_EN,
      item_short_name: source.Item_Short_Name,
      item_unit_in: source.Item_Unit_IN,
      item_unit_st: source.Item_Unit_ST,
      item_unit_out: source.Item_Unit_OUT,
      item_cost: source.Item_Cost,
      item_price: source.Item_Price,
      item_pricea: source.Item_PriceA,
      item_priceb: source.Item_PriceB,
      item_brand: source.Item_Brand,
      item_series: source.Item_Series,
      item_group: source.Item_Group,
      item_cat: source.Item_Cat,
      item_colour: source.Item_Colour,
      item_size: source.Item_Size,
      item_status: source.Item_Status,
      item_popoint: source.Item_PoPoint,
      item_maxonhand: source.Item_MaxOnhand,
      ap_code: source.AP_Code,
      item_gold: source.Item_Gold,
      item_onhand: source.Item_Onhand,
      item_wh: source.WH_Name,
      item_img: source.Item_Img || "",
    });
    setSelectedData(source);
  };

  return (
    <div className="Creditor">
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="btn btn-toggle-sidebar"
        style={{
          position: "fixed",
          top: "1rem",
          left: isSidebarOpen ? "190px" : "-15px",
          zIndex: 1001,
          background: "transparent",
          border: "none",
          color: "#E13F57",
          fontSize: "20px",
        }}
      >
        {isSidebarOpen ? "✖" : "☰"}
      </button>
      <div className="wrapper">
        <Sidebar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <div
          className={`main-panel ${
            isSidebarOpen ? "sidebar-open" : "sidebar-closed"
          }`}
        >
          <div className="container-fluid">
            <div className="page-inner" style={{ marginTop: "56px" }}>
              {mode === "S" ? (
                <Main
                  masterList={dataMasterList}
                  name={"ข้อมูลสินค้า"}
                  onPageInsert={() => onPageInsert()}
                  onRowSelected={(item) => onRowSelected(item)}
                  initialize={initialize}
                  serverSide
                  totalItems={totalItems}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  searchTerm={searchTerm}
                  onSearchChange={handleSearchChange}
                  isLoading={isLoading}
                  groups={groups}
                  filterGroup={filterGroup}
                  onGroupChange={handleGroupChange}
                />
              ) : (
                <Form
                  callInitialize={initialize}
                  mode={mode}
                  name={"ข้อมูลสินค้า"}
                  formData={formData}
                  setFormData={setFormData}
                  selectedData={selectedData}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TbSetItem;
